import React from 'react';
import { getUserClassColor } from '../../utils/chartUtils';


// COMMON CHART COMPONENTS


/**
 * Custom Tooltip Component
 */
export const CustomTooltip = ({ active, payload, label, dataFormat }) => {
  if (!active || !payload || !payload.length) return null;
  // Special handling for Sankey
  if (dataFormat === 'sankey') {
    const data = payload[0]?.payload;
    if (!data) return null;
    
    return (
      <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
        {data.source && data.target ? (
          // Link tooltip
          <>
            <p className="font-semibold text-gray-800 mb-1">Flow</p>
            <p className="text-sm text-gray-700">
              {data.source.name}
            </p>
            <br></br>
            <p className="text-sm text-gray-700">
              {data.target.name}
            </p>
            <p className="text-sm font-medium text-blue-600 mt-1">
              Value: {data.value?.toLocaleString()}
            </p>
            {data.payload.userClass && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-600">Class:</p>
                <span 
                  className="inline-block mt-1 px-2 py-1 rounded text-xs font-medium text-white"
                  style={{ backgroundColor: getUserClassColor(data.payload.userClass, 'node') }}
                >
                  {data.payload.userClass}
                </span>
              </div>
            )}
          </>
        ) : (
          // Node tooltip
          <>
            <p className="font-semibold text-gray-800 mb-1">{data.name}</p>
            <p className="text-sm text-gray-600">
              Total: {data.value?.toLocaleString()}
            </p>
            {data.payload.userClass && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <span 
                  className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                  style={{ backgroundColor: getUserClassColor(data.payload.userClass, 'node') }}
                >
                  {data.payload.userClass}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Standard tooltip for other chart types
  return (
    <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
      <p className="font-semibold text-gray-800 mb-2">
        {dataFormat === 'velocity-pivot' || dataFormat === 'velocity-bucket' || dataFormat === 'kpi-velocity' 
          ? `Speed: ${label}${dataFormat === 'velocity-pivot' || dataFormat === 'velocity-bucket' ? ' mph' : ''}` 
          : `Time: ${label}`}
      </p>
      {/* Show event_time for velocity-pivot data */}
      {payload[0]?.payload?.event_time && dataFormat === 'velocity-pivot' && (
        <p className="text-sm text-gray-600 mb-2">
          Time: {payload[0].payload.event_time}
        </p>
      )}
      {payload.map((entry, index) => (
        <p key={index} style={{ color: entry.color }} className="text-sm">
          {`${entry.name.replace('_', ' ')}: ${typeof entry.value === 'number' 
            ? entry.value.toLocaleString() 
            : entry.value}`}
            {dataFormat === 'velocity-percentile' ? ' mph' : ''} 
        </p>
      ))}
      {payload[0]?.payload?.total && (
        <p className="text-sm font-semibold text-gray-700 mt-1 pt-1 border-t">
          Total: {payload[0].payload.total.toLocaleString()}
        </p>
      )}
    </div>
  );
};

/**
 * Chart Header Component
 */
export const ChartHeader = ({ title, subtitle }) => (
  <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 relative gap-2 mb-4">
    <p className="self-stretch flex-grow-0 flex-shrink-0 text-[21px] font-medium text-left text-[#191d23]">
      {title}
    </p>
    {subtitle && (
      <p className="self-stretch flex-grow-0 flex-shrink-0 text-[16.8px] text-left text-slate-500">
        {subtitle}
      </p>
    )}
  </div>
);

/**
 * Chart Type Selector for Time-Series and Velocity Data
 */
export const TimeSeriesChartSelector = ({ 
  chartType, 
  onChange, 
}) => {
  const buttons = [
    { type: "line", label: "Line Chart" },
    { type: "stacked", label: "Stacked Bar" },
    { type: "area", label: "Stacked Area" }
  ];
  

  return (
    <div className="mb-6 flex gap-2 flex-wrap">
      {buttons.map(({ type, label }) => (
        <button
          key={type}
          onClick={() => onChange(type)}
          className={`px-4 py-2 rounded transition-colors ${
            chartType === type
              ? "bg-blue-500 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

/**
 * Chart Type Selector for Sankey Data
 */
export const SankeyChartSelector = ({ 
  sortBy, 
  onChange, 
  showHeatmap, 
  onHeatmapToggle, 
  viewMode, 
  onViewModeChange,
  data, 
  activeUserFilter, 
  onUserFilterChange,
  availableUserTypes
}) => {
  const buttons = [
    { type: "name", label: "Sort by Name" },
    { type: "value", label: "Sort by Value" }
  ];

  let displayTypes = availableUserTypes || [];

  // Fallback to auto-detection only if not explicitly provided
  if (!displayTypes.length && data && data.length > 0) {
    const firstRowKeys = Object.keys(data[0]).map(k => k.toLowerCase());
    if (firstRowKeys.some(k => k.includes('vehicle') || k.includes('car'))) displayTypes.push('Vehicle');
    if (firstRowKeys.some(k => k.includes('pedestrian') || k.includes('person'))) displayTypes.push('Pedestrian');
    if (firstRowKeys.some(k => k.includes('cyclist') || k.includes('bike'))) displayTypes.push('Cyclist');
  }

  return (
    <div className="mb-6 space-y-3">
      
      {/* User Type Filter Buttons */}
      {displayTypes.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-sm font-medium text-gray-700 mr-2">Filter User:</span>
          
          <button
            onClick={() => onUserFilterChange("All")}
            className={`px-4 py-1.5 text-sm rounded transition-colors ${
              activeUserFilter === "All"
                ? "bg-indigo-500 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            All
          </button>
          
          {displayTypes.map((type) => (
            <button
              key={type}
              onClick={() => onUserFilterChange(type)}
              className={`px-4 py-1.5 text-sm rounded transition-colors ${
                activeUserFilter === type
                  ? "bg-indigo-500 text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="flex gap-2 flex-wrap items-center">
        <span className="text-sm font-medium text-gray-700">View:</span>
        <button
          onClick={() => onViewModeChange('flow')}
          className={`px-4 py-2 rounded transition-colors ${
            viewMode === 'flow'
              ? "bg-blue-500 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          }`}
        >
          Flow Diagram
        </button>
        <button
          onClick={() => onViewModeChange('table')}
          className={`px-4 py-2 rounded transition-colors ${
            viewMode === 'table'
              ? "bg-blue-500 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          }`}
        >
          Heatmap Table
        </button>
      </div>

      {/* Sort and Heatmap Options (only for flow view) */}
      {viewMode === 'flow' && (
        <div className="flex gap-2 flex-wrap items-center">
          {buttons.map(({ type, label }) => (
            <button
              key={type}
              onClick={() => onChange(type)}
              className={`px-4 py-2 rounded transition-colors ${
                sortBy === type
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
            >
              {label}
            </button>
          ))}
          
          <div className="flex items-center gap-2 ml-4 px-3 py-2 bg-gray-100 rounded">
            <input
              type="checkbox"
              id="heatmap-toggle"
              checked={showHeatmap}
              onChange={(e) => onHeatmapToggle(e.target.checked)}
              className="w-4 h-4 accent-blue-500 cursor-pointer"
            />
            <label 
              htmlFor="heatmap-toggle" 
              className="text-sm font-medium text-gray-700 cursor-pointer"
            >
              Heat Map Colors
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Chart Type Selector for KPI Data
 */
export const KPIChartSelector = ({ chartType, onChange }) => {
  const buttons = [
    { type: "stacked", label: "Stacked Bar Chart" },
    { type: "grouped", label: "Grouped Bar Chart" },
    { type: "horizontal", label: "Horizontal Chart" }
  ];

  return (
    <div className="mb-6 flex gap-2 flex-wrap">
      {buttons.map(({ type, label }) => (
        <button
          key={type}
          onClick={() => onChange(type)}
          className={`px-4 py-2 rounded transition-colors ${
            chartType === type
              ? "bg-blue-500 text-white"
              : "bg-gray-200 hover:bg-gray-300"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};